/**
 * Invoice status badges (OR-1449).
 *
 * ⚠️ The design's badge set and what the backend can actually supply are
 * two different vocabularies, so read this before adding a label.
 *
 * The Figma glossary lists ~12 statuses that belong to Ascend's **Program**
 * resource: Payment Due, Active, Processing, Overdue, Pending Cancelation,
 * Reinstatement Due, Pending Reinstatement, Completed, Canceled, Transfer
 * Funds, plus Orion-only Draft and Archived.
 *
 * What Orion stores is `ascend_invoice.ascend_invoice_status`, which is the
 * Ascend **Invoice** status: six values, captured ONCE when the sync job
 * completes. The Program's own `status` is read-only on Ascend's side and
 * is not persisted at all, and there is no webhook or polling refreshing
 * either. So the success toast's promise that the status updates
 * automatically is not something the FE can honour today.
 *
 * This maps the six real values onto the approved labels where they
 * genuinely correspond. It deliberately does NOT invent a label for the
 * window while the sync job is still running, or for a job that failed:
 * neither has a counterpart in the approved set, so those render the same
 * `—` placeholder the rest of the table uses for unknown values. Pending a
 * copy decision from design, not a guess here.
 */

/** Subset of atlas `BadgeColor` used by this table. */
export type InvoiceBadgeColor = "gray" | "blue" | "orange" | "success" | "error" | "warning";

export interface InvoiceBadge {
    label: string;
    color: InvoiceBadgeColor;
}

/**
 * Ascend Invoice status → approved badge. Keys are Ascend's wire values
 * (`src/schemas/v2/ascend/invoices.py`); the labels come from the Figma.
 */
const ASCEND_STATUS_BADGES: Record<string, InvoiceBadge> = {
    draft: { label: "Draft", color: "gray" },
    awaiting_payment: { label: "Payment due", color: "blue" },
    processing_payment: { label: "Processing", color: "blue" },
    overdue: { label: "Overdue", color: "orange" },
    paid: { label: "Completed", color: "success" },
    void: { label: "Canceled", color: "error" },
};

/**
 * Labels the design specifies that have no backend source yet. Kept here
 * so the intent survives, and so whoever wires them later has the colours
 * already decided rather than re-deriving them from screenshots.
 *
 * `Archived` waits on an archive endpoint; the rest wait on the Program
 * status being persisted and kept fresh.
 */
export const UNWIRED_BADGES: Record<string, InvoiceBadge> = {
    incomplete: { label: "Incomplete", color: "gray" },
    active: { label: "Active", color: "success" },
    pending_cancelation: { label: "Pending cancelation", color: "warning" },
    reinstatement_due: { label: "Reinstatement Due", color: "error" },
    pending_reinstatement: { label: "Pending Reinstatement", color: "warning" },
    transfer_funds: { label: "Transfer Funds", color: "blue" },
    archived: { label: "Archived", color: "gray" },
};

/**
 * The badge for a row, or null when there is nothing truthful to show —
 * the caller renders its placeholder. Null happens while the Ascend sync
 * is still in flight, if it failed, or if Ascend returns a status we have
 * not mapped.
 */
export function invoiceBadge(ascendInvoiceStatus: string | null): InvoiceBadge | null {
    if (!ascendInvoiceStatus) return null;
    return ASCEND_STATUS_BADGES[ascendInvoiceStatus.toLowerCase()] ?? null;
}
